import { Component, OnChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { HsgModal } from '@hhsg/hsg-asc';

import { BaseShiftBlockGridComponent } from '../../base-grid/base-shiftblock-grid/base-shiftblock-grid.component';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { InpatientCell } from '../../models/inpatient-cell.model';
import { JobTitle } from 'src/shared/models/job-title.model';
import { ShiftBlock } from 'src/shared/models/shift-block.model';
import { StaffingGridModalComponent } from '../../staffing-grid-modal/staffing-grid-modal.component';
import { StaffingGridUtil } from '../../utilities/staffing-grid.util';

@Component({
    selector: 'smt-variable-jobs-shift-grid',
    templateUrl: './variable-jobs-shift-grid.component.html',
    styleUrls: ['./variable-jobs-shift-grid.component.scss']
})
export class VariableJobsShiftGridComponent extends BaseShiftBlockGridComponent implements OnChanges {
    detailCensusCountFrom: string;
    detailCensusCountTo: string;
    detailJobTitle: string;
    detailShiftBlock: string;
    detailShiftBlockDescription: string;
    detailUseRatio = true;
    detailUseRatioLabel = "Ratio";
    detailValue: number;
    enableShiftBlockModal = false;
    isWeekday = false;
    jobTitles: JobTitle[] = [];
    maxCensus = 0;
    minCensus = 0;
    modalTitle: string;
    quickEditSelect = new UntypedFormControl(-1);
    staffingGrid: InpatientCell[][] = [];

    readonly DetailDefault = "All";
    selectedCensusCounts: number[] = [];

    constructor(private _costCenterConfigurationService: CostCenterConfigurationService, private _modal: HsgModal) {
        super(_costCenterConfigurationService);

        super.initializeGridProcess(() => this.buildVariableStaffingGrid(), () => this.initializeInpatientGrid());
    }

    ngOnChanges() {          
        if (!this.costCenter || !this.costCenter.variableJobTitles || !this.costCenter.inpatientConfiguration) {
            this.jobTitles = [];
            this.maxCensus = 0;
            this.minCensus = 0;
        }
        else {
            this.jobTitles = this.costCenter.variableJobTitles;
            this.maxCensus = this.costCenter.inpatientConfiguration.maxCensus;
            this.minCensus = this.costCenter.inpatientConfiguration.minCensus;
        }

        super.Process();
    }   

    private buildStaffingGridCell(
        census: number, 
        columnId: string, 
        jobTitleId: number,
        rowId: string,
        shiftBlock: ShiftBlock): InpatientCell {
        let cell = new InpatientCell();

        cell.census = census;
        cell.columnId = columnId;
        cell.id = this.getCellId(+rowId, shiftBlock.id, jobTitleId);
        cell.isWeekday = this.isWeekday;
        cell.isWeekend = !this.isWeekday;
        cell.jobTitleId = jobTitleId;
        cell.rowId = rowId;
        cell.shiftBlockId = shiftBlock.id;
        cell.startHour = shiftBlock.startHour;

        if (!(jobTitleId in shiftBlock.staffingGrid)) {
            shiftBlock.staffingGrid[jobTitleId] = {};
        }

        cell.value = shiftBlock.staffingGrid[jobTitleId][census];

        return cell;
    }

    private buildVariableStaffingGrid(): InpatientCell[][] {
        if (!this.jobTitles || !this.shiftBlocks) {
            return;
        }        
        
        let cells = [] as InpatientCell[];        
        let diffCensus = this.maxCensus - this.minCensus;
        
        for (let rowCounter = 0; rowCounter <= diffCensus; rowCounter++) {
            let columnCounter = 0;
           
            this.shiftBlocks.sort(s => s.startHour).forEach(s => {                
                this.jobTitles.forEach(j => {
                    cells.push(
                        this.buildStaffingGridCell(
                            this.maxCensus - rowCounter, 
                            columnCounter.toString(), 
                            j.id, 
                            rowCounter.toString(), 
                            s));

                    ++columnCounter;
                });
            });
        }        
    
        this.staffingGrid = this.sortStaffingGrid(cells);

        return this.staffingGrid;
    }

    cellKeyPressed(currentCell: InpatientCell){
        this.updateCell(currentCell);        
    }   

    detailTypeChange() {
        if (this.detailUseRatio) {
            this.detailUseRatioLabel = "Ratio";
        }
        else {
            this.detailUseRatioLabel = "Total";
        }
    }

    editShiftBlock() {
        this.detailCensusCountFrom = this.DetailDefault;
        this.detailCensusCountTo = this.DetailDefault;
        this.detailJobTitle = this.DetailDefault;
        this.detailValue = 0; 
        this.enableShiftBlockModal = true;
    
        if (this.quickEditSelect.value >= 0) {
            let shiftBlock = this.shiftBlocks.find(s => s.startHour == this.quickEditSelect.value);
    
            this.detailShiftBlock = this.quickEditSelect.value.toString();
            this.detailShiftBlockDescription = shiftBlock.description;
        }
        else {
            this.detailShiftBlock = this.DetailDefault;
            this.detailShiftBlockDescription = this.DetailDefault;
        }
    
        // prevent default 'submit' action from firing
        return false;
    }

    private getCellId(census: number, shiftBlockId: number, jobTitleId: number): string {
        return census + '-' + shiftBlockId + '-' + jobTitleId;
    }

    initializeInpatientGrid() {
        this.setShiftBlocksFromSchedule();
        this.setByType();
        this.selectedCensusCounts =  Array((this.costCenter.inpatientConfiguration.maxCensus - this.costCenter.inpatientConfiguration.minCensus) + 1)
                                    .fill(0).map((x, i)=> this.costCenter.inpatientConfiguration.maxCensus - i);          
    }

    onCopy() {
        this.setShiftBlocksFromCopy();
    
        this.copyShiftBlocks.forEach(sourceBlock => {
            let targetBlock = this.shiftBlocks.find(b => b.startHour === sourceBlock.startHour);
    
            this.jobTitles.forEach(jobTitle => {
                if (jobTitle.id in targetBlock.staffingGrid && jobTitle.id in sourceBlock.staffingGrid) {
                    targetBlock.staffingGrid[jobTitle.id] = {};
    
                    Object.keys(sourceBlock.staffingGrid[jobTitle.id]).forEach(censusCount => {
                        targetBlock.staffingGrid[jobTitle.id][censusCount] = sourceBlock.staffingGrid[jobTitle.id][censusCount];
                    });
                }
            });
        });
    
        this.staffingGrid = this.buildVariableStaffingGrid();
        this._costCenterConfigurationService.announceFormIsDirty(true);    
        return false;
    }

    onViewFullStaffingGrid() {
        this._modal.open(
            StaffingGridModalComponent, 
            {
                data: {
                    title: this.modalTitle,
                    size: { width: '100%', height: '100%' },
                    isVariable: true,
                    minCensus: this.minCensus,
                    maxCensus: this.maxCensus,
                    jobTitles: this.jobTitles,
                    selectedShiftBlocks: this.shiftBlocks,
                    selectedMaxCensus: this.maxCensus,
                    selectedCensusTable: this.staffingGrid
                }
            });
        return false;
    }

    saveQuickEdit() {
        this.enableShiftBlockModal = false;
    
        let censusCountFrom = this.detailCensusCountFrom || this.DetailDefault;
        let censusCountTo = this.detailCensusCountTo || this.DetailDefault;
        let jobTitle = this.detailJobTitle;
        let numberValue = this.detailValue;
        let shiftBlock = this.detailShiftBlock;
    
        if (!this.staffingGrid || this.staffingGrid.length == 0) {
            return;
        }
        
        this.staffingGrid.forEach(row => {
            let cells = row.filter(c =>
                (censusCountFrom == this.DetailDefault || +c.census >= +censusCountFrom)  &&
                (censusCountTo == this.DetailDefault || +c.census <= +censusCountTo) &&
                (jobTitle == this.DetailDefault || c.jobTitleId == +jobTitle) &&
                (shiftBlock == "All" || c.startHour == +shiftBlock));
            
            cells.forEach(cell => {
                if (this.detailUseRatio) {
                    cell.value = numberValue === 0 ? 0 : Math.ceil(+cell.census / numberValue);                    
                } 
                else {
                    cell.value = numberValue;
                }

                this.updateCell(cell);
            });
        });
    }    

    private setByType() {
        this.isWeekday = StaffingGridUtil.isGridTypeWeekday(this.gridType);

        if (this.isWeekday) {
            this.modalTitle = "Weekday";
        }
        else {
            this.modalTitle = "Weekend";
        }

        this.modalTitle += " Variable Staffing Grid";       
    }

    private setShiftBlocksForSave() {
        if (this.shiftBlocks) {
            this.shiftSchedule.partialShiftBlocks = this.shiftBlocks;
        }        
    }

    private sortStaffingGrid(cells: InpatientCell[]): InpatientCell[][] {
        let diffCensus = this.maxCensus - this.minCensus;

        let sortedTable: InpatientCell[][] = []; 

        for (let counter = 0; counter <= diffCensus; counter++) {
            let censusCount = this.maxCensus - counter;
            let row = cells.filter(c => c.census == censusCount);
            let sortedRow: InpatientCell[] = [];
    
            this.shiftBlocks.sort(s => s.startHour).forEach(s => {
                let columnCounter = 0;
                
                this.jobTitles.forEach(j => {                    
                    let cell = row.find(c => c.jobTitleId == j.id && c.shiftBlockId == s.id);
    
                    if (cell) {
                        sortedRow.push(cell);
                    } 
                    else {
                        let newCell = new InpatientCell();

                        newCell.census = censusCount;
                        newCell.rowId = counter.toString();
                        newCell.columnId = columnCounter.toString();
                        newCell.shiftBlockId = s.id;
                        newCell.startHour = s.startHour;
                        newCell.value = s.staffingGrid[j.id][censusCount];
                        sortedRow.push(newCell);

                        ++columnCounter;
                    }
                });
            });
    
            sortedTable.push(sortedRow);
        }
    
        return sortedTable;
    }   
    
    private updateCell(currentCell: InpatientCell) {
        let shiftBlock = this.shiftBlocks.find(b => b.startHour === currentCell.startHour);
    
        if (!shiftBlock) {
            return;
        }

        let staffCount = currentCell.value;
    
        if (!shiftBlock.staffingGrid) {
            shiftBlock.staffingGrid = {};
        }
    
        if (!StaffingGridUtil.gridContainsValidJobTitle(currentCell.jobTitleId, shiftBlock, false)) {
            shiftBlock.staffingGrid[currentCell.jobTitleId] = {};
        }
    
        if (staffCount || staffCount === 0) {
            staffCount = +staffCount;
        } 
        else {
            staffCount = null;
        }
    
        shiftBlock.staffingGrid[currentCell.jobTitleId][currentCell.census] = staffCount;

        StaffingGridUtil.checkCompleteJobsShiftGrid(
            this.costCenter.variableJobTitles, 
            this.shiftSchedule,
            false,
            this.maxCensus,
            this.minCensus);

        this.setShiftBlocksForSave();

        this._costCenterConfigurationService.announceFormIsDirty(true);
    }
}
